import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import RichTextEditor from '../components/RichTextEditor';
import ProductMediaUploader from '../components/ProductMediaUploader';
import ProductVersionHistory from '../components/ProductVersionHistory';
import ProductAIRewriteModal from '../components/ProductAIRewriteModal';
import ContentPicker from '../components/ContentPicker';
import VoiceButton from '../components/ui/VoiceButton';
import {
    ChevronLeft, Save, Send, MoreHorizontal, Trash2, Package, Tag, DollarSign,
    Globe, Plus, X, MapPin, Info, Image as ImageIcon, GitBranch, Clock,
    CheckCircle2, History, Settings, GripVertical, ChevronDown, ChevronUp,
    FileText, List, Wrench, Eye, HelpCircle, RefreshCw, Sparkles, Bot, LayoutTemplate, ArrowRight, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback defaults (overridden by org settings from API)
const DEFAULT_CATEGORIES = [
    { value: 'workshops', label: 'Workshops' },
    { value: 'ai_consulting', label: 'AI Consulting' },
    { value: 'software', label: 'Software' },
    { value: 'certifications', label: 'Certifications' },
    { value: 'embedded_teams', label: 'Embedded Teams' },
];

const STATUSES = [
    { value: 'active', label: 'Active', color: 'bg-emerald-50 text-emerald-700' },
    { value: 'coming_soon', label: 'Coming Soon', color: 'bg-amber-50 text-amber-700' },
    { value: 'archived', label: 'Archived', color: 'bg-stone-100 text-stone-500' },
];

const DEFAULT_PRICING_MODELS = [
    { value: 'per_participant', label: 'Per Participant (Public)' },
    { value: 'flat_fee', label: 'Flat Fee (Private)' },
    { value: 'per_event', label: 'Per Event' },
];

const SAAS_PRICING_MODELS = [
    { value: 'subscription', label: 'Subscription' },
    { value: 'per_seat', label: 'Per Seat / Per User' },
    { value: 'tiered', label: 'Tiered Plans' },
    { value: 'usage_based', label: 'Usage-Based / Pay-As-You-Go' },
    { value: 'freemium', label: 'Freemium' },
    { value: 'flat_rate', label: 'Flat Rate' },
    { value: 'per_feature', label: 'Per Feature / Add-On' },
    { value: 'one_time_license', label: 'One-Time License' },
];

const SAAS_BILLING_INTERVALS = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'semi_annual', label: 'Semi-Annual' },
    { value: 'annual', label: 'Annual' },
    { value: 'one_time', label: 'One-Time' },
    { value: 'custom', label: 'Custom' },
];

const CURRENCIES = [
    { value: 'USD', label: 'USD ($)', symbol: '$' },
    { value: 'EUR', label: 'EUR (\u20ac)', symbol: '\u20ac' },
    { value: 'GBP', label: 'GBP (\u00a3)', symbol: '\u00a3' },
    { value: 'AUD', label: 'AUD (A$)', symbol: 'A$' },
];

const REGIONS = [
    { value: 'UAE', label: 'UAE', defaultCurrency: 'USD' },
    { value: 'Saudi Arabia', label: 'Saudi Arabia', defaultCurrency: 'USD' },
    { value: 'Qatar', label: 'Qatar', defaultCurrency: 'USD' },
    { value: 'USA', label: 'USA', defaultCurrency: 'USD' },
    { value: 'Australia', label: 'Australia', defaultCurrency: 'AUD' },
    { value: 'New Zealand', label: 'New Zealand', defaultCurrency: 'USD' },
    { value: 'United Kingdom', label: 'United Kingdom', defaultCurrency: 'GBP' },
];

const SECTION_TYPES = {
    rich_text: { icon: FileText, label: 'Rich Text' },
    features: { icon: List, label: 'Features' },
    key_value: { icon: Wrench, label: 'Key-Value / Specs' },
    media_gallery: { icon: ImageIcon, label: 'Media Gallery' },
};

const currencySymbol = (code) => CURRENCIES.find(c => c.value === code)?.symbol || '$';

// ─── Section Editors ────────────────────────────────────────────────────────

function RichTextSection({ section, onChange }) {
    return (
        <RichTextEditor
            content={section.content_html || ''}
            onChange={(html) => onChange({ ...section, content_html: html })}
            placeholder="Write detailed content here..."
            minHeight="200px"
        />
    );
}

function FeaturesSection({ section, onChange }) {
    const features = section.data?.features || [];

    const addFeature = () => {
        const updated = [...features, { title: '', description: '' }];
        onChange({ ...section, data: { ...section.data, features: updated } });
    };

    const updateFeature = (idx, field, value) => {
        const updated = features.map((f, i) => i === idx ? { ...f, [field]: value } : f);
        onChange({ ...section, data: { ...section.data, features: updated } });
    };

    const removeFeature = (idx) => {
        const updated = features.filter((_, i) => i !== idx);
        onChange({ ...section, data: { ...section.data, features: updated } });
    };

    return (
        <div className="space-y-2">
            {features.map((feat, idx) => (
                <div key={idx} className="flex items-start gap-2 p-3 bg-stone-50 rounded-lg border border-stone-100">
                    <div className="flex-1 space-y-1.5">
                        <div className="flex items-center gap-1.5">
                            <Input
                                value={feat.title}
                                onChange={e => updateFeature(idx, 'title', e.target.value)}
                                placeholder="Feature title"
                                className="h-8 text-sm font-medium flex-1"
                            />
                            <VoiceButton onTranscript={t => updateFeature(idx, 'title', feat.title ? feat.title + ' ' + t : t)} />
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Input
                                value={feat.description}
                                onChange={e => updateFeature(idx, 'description', e.target.value)}
                                placeholder="Brief description (optional)"
                                className="h-8 text-sm flex-1"
                            />
                            <VoiceButton onTranscript={t => updateFeature(idx, 'description', feat.description ? feat.description + ' ' + t : t)} />
                        </div>
                    </div>
                    <button onClick={() => removeFeature(idx)} className="p-1.5 hover:bg-red-50 rounded text-stone-400 hover:text-red-500 mt-1">
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ))}
            <button onClick={addFeature} className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 px-3 py-2 hover:bg-stone-50 rounded-lg transition-colors">
                <Plus className="w-3.5 h-3.5" /> Add Feature
            </button>
        </div>
    );
}

function KeyValueSection({ section, onChange }) {
    const pairs = section.data?.pairs || [];

    const addPair = () => {
        const updated = [...pairs, { key: '', value: '' }];
        onChange({ ...section, data: { ...section.data, pairs: updated } });
    };

    const updatePair = (idx, field, value) => {
        const updated = pairs.map((p, i) => i === idx ? { ...p, [field]: value } : p);
        onChange({ ...section, data: { ...section.data, pairs: updated } });
    };

    const removePair = (idx) => {
        const updated = pairs.filter((_, i) => i !== idx);
        onChange({ ...section, data: { ...section.data, pairs: updated } });
    };

    return (
        <div className="space-y-3">
            {/* Help text */}
            <div className="flex items-start gap-2 p-3 bg-stone-50 border border-stone-100 rounded-lg">
                <HelpCircle className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-stone-500">
                    <p className="font-medium text-stone-600 mb-1">Technical Specifications & Details</p>
                    <p>Add structured information as label-value pairs. Useful for technical specs, dimensions, compatibility info, or any product attributes that don't fit in the main description.</p>
                    <p className="mt-1 text-stone-400">Examples: <span className="font-mono text-[10px] bg-stone-100 px-1 rounded">Platform → Web, iOS, Android</span> · <span className="font-mono text-[10px] bg-stone-100 px-1 rounded">Version → 3.2.1</span> · <span className="font-mono text-[10px] bg-stone-100 px-1 rounded">Max Users → 500</span></p>
                </div>
            </div>

            {/* Header row */}
            {pairs.length > 0 && (
                <div className="flex items-center gap-2 px-1">
                    <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider flex-1">Label</span>
                    <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider flex-1">Value</span>
                    <span className="w-8" />
                </div>
            )}

            {pairs.map((pair, idx) => (
                <div key={idx} className="flex items-center gap-2">
                    <Input
                        value={pair.key}
                        onChange={e => updatePair(idx, 'key', e.target.value)}
                        placeholder="e.g., Platform"
                        className="h-8 text-sm flex-1"
                    />
                    <Input
                        value={pair.value}
                        onChange={e => updatePair(idx, 'value', e.target.value)}
                        placeholder="e.g., Web, iOS, Android"
                        className="h-8 text-sm flex-1"
                    />
                    <button onClick={() => removePair(idx)} className="p-1.5 hover:bg-red-50 rounded text-stone-400 hover:text-red-500">
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ))}
            <button onClick={addPair} className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 px-3 py-2 hover:bg-stone-50 rounded-lg border border-dashed border-stone-200 rounded-lg transition-colors w-full justify-center">
                <Plus className="w-3.5 h-3.5" /> Add Specification
            </button>
        </div>
    );
}

function SectionCard({ section, onUpdate, onRemove, productId, getAuthHeader }) {
    const [collapsed, setCollapsed] = useState(false);
    const TypeIcon = SECTION_TYPES[section.type]?.icon || FileText;

    const renderEditor = () => {
        switch (section.type) {
            case 'rich_text':
                return <RichTextSection section={section} onChange={onUpdate} />;
            case 'features':
                return <FeaturesSection section={section} onChange={onUpdate} />;
            case 'key_value':
                return <KeyValueSection section={section} onChange={onUpdate} />;
            case 'media_gallery':
                return <ProductMediaUploader productId={productId} getAuthHeader={getAuthHeader} />;
            default:
                return <RichTextSection section={section} onChange={onUpdate} />;
        }
    };

    return (
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-100 bg-stone-50/50">
                <GripVertical className="w-4 h-4 text-stone-300 cursor-grab" />
                <TypeIcon className="w-4 h-4 text-stone-400" />
                <input
                    value={section.title}
                    onChange={e => onUpdate({ ...section, title: e.target.value })}
                    className="flex-1 text-sm font-semibold text-stone-700 bg-transparent border-none outline-none focus:ring-0 p-0"
                    placeholder="Section title"
                />
                <button onClick={() => setCollapsed(!collapsed)} className="p-1 hover:bg-stone-100 rounded text-stone-400">
                    {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                </button>
                <button onClick={onRemove} className="p-1 hover:bg-red-50 rounded text-stone-400 hover:text-red-500">
                    <X className="w-4 h-4" />
                </button>
            </div>
            {!collapsed && (
                <div className="p-4">
                    {renderEditor()}
                </div>
            )}
        </div>
    );
}


// ─── Main ProductDetail Component ───────────────────────────────────────────

export default function ProductDetail() {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const { can } = usePermissions();
    const isNew = productId === 'new';

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [showVersionHistory, setShowVersionHistory] = useState(false);
    const [showAddSection, setShowAddSection] = useState(false);
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [showMdPreview, setShowMdPreview] = useState(false);
    const [mdContent, setMdContent] = useState('');

    const loadMdPreview = async () => {
        setShowMdPreview(true);
        setMdContent('');
        try {
            const res = await axios.get(`${API}/products/ai/md-preview/${productId}`, { headers: getAuthHeader() });
            setMdContent(res.data.md || 'No content generated yet.');
        } catch {
            setMdContent('Failed to load MD preview. Save and publish the product first.');
        }
    };
    const moreRef = useRef(null);

    // Admin-configurable settings (loaded from API)
    const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
    const [pricingModels, setPricingModels] = useState(DEFAULT_PRICING_MODELS);

    // Product form state
    const [form, setForm] = useState({
        name: '', sku: '', category: '', status: 'active',
        description: '', pricing_model: '', currency: 'USD',
        amount: '', billing_frequency: '', region_pricing: [],
        base_info_html: '', ai_rewrite_result: null, ai_persona_results: null,
        content_html: '', sections: [], tags: [],
        featured_image_url: null,
        track_inventory: false, inventory_type: null, unit_of_measure: null,
        min_seats: '', max_seats: '', free_tier_limits: '', trial_period: '', setup_fee: '',
    });
    const [overviewTab, setOverviewTab] = useState('base_info');
    const [showAIRewriteModal, setShowAIRewriteModal] = useState(false);
    const [product, setProduct] = useState(null);
    const [skuManual, setSkuManual] = useState(false);
    const [skuLoading, setSkuLoading] = useState(false);
    const [skuError, setSkuError] = useState('');

    // Auto-generate SKU when category changes (new products only)
    const generateSku = async (category) => {
        if (!category) return;
        setSkuLoading(true);
        setSkuError('');
        try {
            const res = await axios.get(`${API}/products/next-sku?category=${category}`, { headers: getAuthHeader() });
            setForm(prev => ({ ...prev, sku: res.data.sku }));
            setSkuManual(false);
        } catch { setSkuError('Failed to generate SKU'); }
        finally { setSkuLoading(false); }
    };

    const handleCategoryChange = (category) => {
        setForm(prev => ({ ...prev, category }));
        // Auto-generate SKU for new products when category changes (unless user manually edited)
        if (isNew && !skuManual) {
            generateSku(category);
        }
    };

    // Load product settings (categories, pricing models) from org config
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/products/settings`, { headers: getAuthHeader() });
                if (res.data.categories?.length) setCategories(res.data.categories);
                if (res.data.pricing_models?.length) setPricingModels(res.data.pricing_models);
                // Set defaults for new product from first options + auto-generate SKU
                if (isNew) {
                    const defaultCat = res.data.categories?.[0]?.value || '';
                    setForm(prev => ({
                        ...prev,
                        category: prev.category || defaultCat,
                        pricing_model: prev.pricing_model || res.data.pricing_models?.[0]?.value || '',
                    }));
                    if (defaultCat) generateSku(defaultCat);
                }
            } catch {}
        })();
    }, []);

    // Load product
    useEffect(() => {
        if (!isNew) loadProduct();
    }, [productId]);

    const loadProduct = async () => {
        try {
            const res = await axios.get(`${API}/products/${productId}`, { headers: getAuthHeader() });
            const p = res.data || {};
            setProduct(p);
            setForm({
                name: p.name || '', sku: p.sku || '', category: p.category || 'workshops',
                status: p.status || 'active', description: p.description || '',
                pricing_model: p.pricing_model || 'per_event', currency: p.currency || 'USD',
                amount: p.amount != null ? String(p.amount) : '', billing_frequency: p.billing_frequency || '',
                region_pricing: Array.isArray(p.region_pricing) ? p.region_pricing : [],
                base_info_html: p.base_info_html || '', ai_rewrite_result: p.ai_rewrite_result || null, ai_persona_results: p.ai_persona_results || null,
                content_html: p.content_html || '',
                sections: Array.isArray(p.sections) ? p.sections : [],
                tags: Array.isArray(p.tags) ? p.tags : [],
                featured_image_url: p.featured_image_url || null,
                track_inventory: p.track_inventory || false,
                inventory_type: p.inventory_type || null,
                unit_of_measure: p.unit_of_measure || null,
                min_seats: p.min_seats != null ? String(p.min_seats) : '',
                max_seats: p.max_seats != null ? String(p.max_seats) : '',
                free_tier_limits: p.free_tier_limits || '', trial_period: p.trial_period || '',
                setup_fee: p.setup_fee != null ? String(p.setup_fee) : '',
            });
        } catch (err) {
            console.error('Failed to load product:', err?.response?.status, err?.response?.data || err?.message);
            toast.error('Failed to load product');
            navigate('/products');
        } finally {
            setLoading(false);
        }
    };

    // Build save payload from current form state
    const buildPayload = () => ({
        ...form,
        amount: parseFloat(form.amount) || 0,
        min_seats: form.min_seats ? parseInt(form.min_seats) : null,
        max_seats: form.max_seats ? parseInt(form.max_seats) : null,
        setup_fee: form.setup_fee ? parseFloat(form.setup_fee) : null,
        sections: form.sections.map((s, i) => ({ ...s, order: i })),
    });

    // Save as draft (used by AI writer to auto-save unsaved products)
    // Returns the new product_id, or existing productId if already saved
    const saveDraft = async () => {
        if (!isNew) return productId;
        const payload = buildPayload();
        if (!payload.status || payload.status === 'active') payload.status = 'active';
        const res = await axios.post(`${API}/products`, payload, { headers: getAuthHeader() });
        const newId = res.data.product_id;
        toast.success('Product auto-saved as draft');
        navigate(`/products/${newId}`, { replace: true });
        return newId;
    };

    // Save (create or update)
    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = buildPayload();
            if (isNew) {
                const res = await axios.post(`${API}/products`, payload, { headers: getAuthHeader() });
                toast.success('Product created');
                navigate(`/products/${res.data.product_id}`, { replace: true });
            } else {
                await axios.put(`${API}/products/${productId}`, payload, { headers: getAuthHeader() });
                toast.success('Product saved');
                loadProduct();
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save product');
        } finally {
            setSaving(false);
        }
    };

    // Publish (generate MD)
    const handlePublish = async () => {
        if (isNew) {
            return toast.error('Save the product first before publishing');
        }
        setPublishing(true);
        try {
            // Save first using the same payload builder as handleSave
            const payload = buildPayload();
            await axios.put(`${API}/products/${productId}`, payload, { headers: getAuthHeader() });
            // Then publish
            await axios.post(`${API}/products/${productId}/publish`, {}, { headers: getAuthHeader() });
            toast.success('Product published — MD file generated for AI agents');
            loadProduct();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to publish');
        } finally {
            setPublishing(false);
        }
    };

    // Delete
    const handleDelete = async () => {
        if (!window.confirm('Delete this product? This will remove all version history and media.')) return;
        try {
            await axios.delete(`${API}/products/${productId}`, { headers: getAuthHeader() });
            toast.success('Product deleted');
            navigate('/products');
        } catch {
            toast.error('Failed to delete product');
        }
    };

    // Sections
    const addSection = (type) => {
        const typeInfo = SECTION_TYPES[type];
        const newSection = {
            section_id: `sec_${Date.now()}`,
            type,
            title: typeInfo?.label || 'New Section',
            order: form.sections.length,
            content_html: '',
            data: type === 'features' ? { features: [] } : type === 'key_value' ? { pairs: [] } : null,
            visible: true,
        };
        setForm(prev => ({ ...prev, sections: [...prev.sections, newSection] }));
        setShowAddSection(false);
    };

    const updateSection = (idx, updated) => {
        setForm(prev => ({
            ...prev,
            sections: prev.sections.map((s, i) => i === idx ? updated : s),
        }));
    };

    const removeSection = (idx) => {
        setForm(prev => ({
            ...prev,
            sections: prev.sections.filter((_, i) => i !== idx),
        }));
    };

    // Region pricing
    const addRegion = (regionValue) => {
        if (form.region_pricing.some(rp => rp.region === regionValue)) return;
        const regionDef = REGIONS.find(r => r.value === regionValue);
        setForm(prev => ({
            ...prev,
            region_pricing: [...prev.region_pricing, {
                region: regionValue,
                currency: regionDef?.defaultCurrency || 'USD',
                amount: 0,
            }],
        }));
    };

    const updateRegionPricing = (idx, field, value) => {
        setForm(prev => ({
            ...prev,
            region_pricing: prev.region_pricing.map((rp, i) =>
                i === idx ? { ...rp, [field]: field === 'amount' ? (parseFloat(value) || 0) : value } : rp
            ),
        }));
    };

    const removeRegion = (idx) => {
        setForm(prev => ({ ...prev, region_pricing: prev.region_pricing.filter((_, i) => i !== idx) }));
    };

    // Tags
    const [tagInput, setTagInput] = useState('');
    const addTag = () => {
        const tag = tagInput.trim();
        if (tag && !form.tags.includes(tag)) {
            setForm(prev => ({ ...prev, tags: [...prev.tags, tag] }));
        }
        setTagInput('');
    };

    // Close more menu on outside click
    useEffect(() => {
        const handler = (e) => {
            if (moreRef.current && !moreRef.current.contains(e.target)) setShowMoreMenu(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const availableRegions = REGIONS.filter(r => !form.region_pricing.some(rp => rp.region === r.value));
    const statusStyle = STATUSES.find(s => s.value === form.status) || STATUSES[0];

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="animate-fade-in">
            {/* Top Bar */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <button
                        onClick={() => navigate('/products')}
                        className="flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 transition-colors flex-shrink-0"
                    >
                        <ChevronLeft className="w-4 h-4" /> Products
                    </button>
                    <span className="text-stone-300">/</span>
                    <input
                        value={form.name}
                        onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Product Name"
                        className="text-xl font-bold text-stone-900 bg-transparent border-none outline-none flex-1 min-w-0 p-0"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                        data-testid="product-name-input"
                    />
                    <VoiceButton onTranscript={t => setForm(prev => ({ ...prev, name: prev.name ? prev.name + ' ' + t : t }))} />
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {!isNew && product?.version > 1 && (
                        <span className="flex items-center gap-1 text-xs text-stone-400 mr-1">
                            <GitBranch className="w-3.5 h-3.5" /> v{product.version}
                        </span>
                    )}
                    {!isNew && (
                        <Button variant="outline" size="sm" onClick={() => setShowVersionHistory(true)} className="text-xs">
                            <History className="w-3.5 h-3.5 mr-1.5" /> History
                        </Button>
                    )}
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        data-testid="save-product-btn"
                    >
                        <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                        onClick={handlePublish}
                        disabled={publishing || isNew}
                        size="sm"
                        className="bg-stone-900 hover:bg-stone-800 text-xs"
                        data-testid="publish-product-btn"
                    >
                        <Send className="w-3.5 h-3.5 mr-1.5" /> {publishing ? 'Publishing...' : 'Publish'}
                    </Button>
                    {!isNew && (
                        <div className="relative" ref={moreRef}>
                            <Button variant="outline" size="sm" onClick={() => setShowMoreMenu(!showMoreMenu)} className="px-2">
                                <MoreHorizontal className="w-4 h-4" />
                            </Button>
                            {showMoreMenu && (
                                <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg py-1 z-20 w-44">
                                    {can('products:delete') && (
                                        <button
                                            onClick={() => { setShowMoreMenu(false); handleDelete(); }}
                                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" /> Delete Product
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="flex gap-6">
                {/* Left Column — Sections */}
                <div className="flex-1 min-w-0 space-y-4">
                    {/* Product Overview — Tabbed Interface */}
                    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                        {/* Step Header */}
                        <div className="flex items-center justify-between px-4 border-b border-stone-100 bg-stone-50/50">
                            <div className="flex items-center gap-0">
                                {[
                                    { key: 'base_info', step: 1, label: 'Base', icon: FileText },
                                    { key: 'agent_writer', step: 2, label: 'Agent Writer', icon: Bot },
                                    { key: 'content', step: 3, label: 'Final Content', icon: LayoutTemplate },
                                ].map(tab => {
                                    const isActive = overviewTab === tab.key;
                                    const hasContent = tab.key === 'base_info' ? !!form.base_info_html?.replace(/<[^>]*>/g, '').trim()
                                        : tab.key === 'agent_writer' ? !!(form.ai_persona_results && Object.keys(form.ai_persona_results).length > 0)
                                        : !!form.content_html?.replace(/<[^>]*>/g, '').trim();
                                    return (
                                        <button key={tab.key} onClick={() => setOverviewTab(tab.key)}
                                            className={`flex items-center gap-1.5 px-3 py-3 text-xs font-medium border-b-2 transition-colors ${
                                                isActive ? 'border-stone-900 text-stone-900' : 'border-transparent text-stone-400 hover:text-stone-600'
                                            }`}>
                                            <span className={`w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center ${
                                                hasContent ? 'bg-emerald-500 text-white' : isActive ? 'bg-stone-900 text-white' : 'bg-stone-200 text-stone-500'
                                            }`}>{hasContent ? '✓' : tab.step}</span>
                                            <tab.icon className="w-3.5 h-3.5" />
                                            {tab.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex items-center gap-2">
                                {!isNew && (
                                    <button onClick={loadMdPreview}
                                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-stone-500 border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors"
                                        title="Preview the MD file that AI agents read">
                                        <Eye className="w-3.5 h-3.5" /> Agent MD
                                    </button>
                                )}
                                <button onClick={() => setShowAIRewriteModal(true)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors">
                                    <Sparkles className="w-3.5 h-3.5" />
                                    Rewrite with AI
                                </button>
                            </div>
                        </div>

                        {/* Tab Content */}
                        <div className="p-4">
                            {/* Base Info Tab */}
                            {overviewTab === 'base_info' && (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <Label className="text-xs text-stone-500">Raw Product Information</Label>
                                        <VoiceButton onTranscript={t => setForm(prev => ({ ...prev, base_info_html: (prev.base_info_html || '') + ' ' + t }))} />
                                    </div>
                                    <RichTextEditor
                                        content={form.base_info_html}
                                        onChange={(html) => setForm(prev => ({ ...prev, base_info_html: html }))}
                                        placeholder="Dump all raw product information here — features, benefits, target audience, competitive advantages, pricing context, use cases, technical specs, testimonials, anything relevant..."
                                        minHeight="300px"
                                    />
                                    <p className="text-[10px] text-stone-400 mt-2">Add all raw information about your product here. Use voice-to-text or paste content. The AI Agent Writer will transform this into structured marketing content.</p>
                                </div>
                            )}

                            {/* Content Tab (existing fields) */}
                            {overviewTab === 'content' && (
                                <div className="space-y-3">
                                    <div>
                                        <div className="flex items-center justify-between">
                                            <Label className="text-xs text-stone-500">Short Description</Label>
                                            <VoiceButton onTranscript={t => setForm(prev => ({ ...prev, description: prev.description ? prev.description + ' ' + t : t }))} />
                                        </div>
                                        <Textarea
                                            value={form.description}
                                            onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                                            placeholder="Brief product description for cards and summaries..."
                                            className="mt-1"
                                            rows={2}
                                            data-testid="product-desc-input"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-stone-500">Detailed Content</Label>
                                        <div className="mt-1">
                                            <RichTextEditor
                                                content={form.content_html}
                                                onChange={(html) => setForm(prev => ({ ...prev, content_html: html }))}
                                                placeholder="Write detailed marketing content, features, benefits, use cases..."
                                                minHeight="250px"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Agent Writer Tab — Multi-persona AI output */}
                            {overviewTab === 'agent_writer' && (
                                <div>
                                    {/* Agent access info banner */}
                                    <div className="flex items-start gap-2 p-3 mb-4 bg-blue-50 border border-blue-200 rounded-lg">
                                        <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                                        <div className="text-xs text-blue-700">
                                            <p className="font-medium mb-1">This content is read by AI agents when published</p>
                                            <p className="text-blue-600">Sales Copilot, Meeting Copilot, Email Writer, and Communication Coach all access the published product MD file. Each persona version gives agents different messaging angles depending on who they're helping you talk to.</p>
                                        </div>
                                    </div>

                                    {(() => {
                                        const personas = form.ai_persona_results || (form.ai_rewrite_result ? { default: form.ai_rewrite_result } : null);
                                        if (!personas || Object.keys(personas).length === 0) {
                                            return (
                                                <div className="flex flex-col items-center justify-center py-16 text-center">
                                                    <Sparkles className="w-12 h-12 text-stone-300 mb-3" />
                                                    <p className="text-sm text-stone-500 mb-1">No AI-generated content yet</p>
                                                    <p className="text-xs text-stone-400 mb-4">Add raw product information in the Base tab, then generate all 4 persona versions.</p>
                                                    <button onClick={() => setShowAIRewriteModal(true)}
                                                        className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800">
                                                        <Sparkles className="w-4 h-4" /> Generate All Personas
                                                    </button>
                                                </div>
                                            );
                                        }

                                        const PERSONA_META = {
                                            default: { label: 'Default — Balanced & Strategic', color: 'stone', icon: '🎯' },
                                            technical: { label: 'Technical Writer — Precise & Detailed', color: 'blue', icon: '⚙️' },
                                            executive: { label: 'Executive Brief — Outcome-Focused', color: 'purple', icon: '📊' },
                                            casual: { label: 'Casual — Friendly & Approachable', color: 'amber', icon: '💬' },
                                        };
                                        const PERSONA_ORDER = ['default', 'technical', 'executive', 'casual'];
                                        const availablePersonas = PERSONA_ORDER.filter(k => personas[k]);

                                        return (
                                            <div className="space-y-4">
                                                {/* Actions bar */}
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-stone-500">{availablePersonas.length} of 4 personas generated</span>
                                                        {availablePersonas.length < 4 && (
                                                            <button onClick={() => setShowAIRewriteModal(true)}
                                                                className="text-xs text-amber-600 hover:text-amber-700 font-medium">Generate missing</button>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button onClick={() => {
                                                            const r = personas.default || personas[availablePersonas[0]];
                                                            if (!r) return;
                                                            setForm(prev => {
                                                                const updated = { ...prev, description: r.short_version || prev.description, content_html: r.marketing_page_html || prev.content_html };
                                                                if (r.features?.length) {
                                                                    const existingIdx = prev.sections.findIndex(s => s.type === 'features');
                                                                    const featuresSection = { section_id: existingIdx >= 0 ? prev.sections[existingIdx].section_id : `sec_${Date.now()}`, type: 'features', title: 'Features', order: existingIdx >= 0 ? prev.sections[existingIdx].order : prev.sections.length, data: { features: r.features }, visible: true };
                                                                    const sections = [...prev.sections];
                                                                    if (existingIdx >= 0) sections[existingIdx] = featuresSection;
                                                                    else sections.push(featuresSection);
                                                                    updated.sections = sections;
                                                                }
                                                                return updated;
                                                            });
                                                            setOverviewTab('content');
                                                            toast.success('Default persona committed to Final Content');
                                                        }}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700">
                                                            <ArrowRight className="w-3 h-3" /> Commit Default to Step 3
                                                        </button>
                                                        <button onClick={() => setShowAIRewriteModal(true)}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 border border-stone-200 text-stone-600 text-xs font-medium rounded-lg hover:bg-stone-50">
                                                            <Sparkles className="w-3 h-3" /> Regenerate
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Persona cards */}
                                                {availablePersonas.map(key => {
                                                    const r = personas[key];
                                                    const meta = PERSONA_META[key] || { label: key, color: 'stone', icon: '📝' };
                                                    return (
                                                        <details key={key} open={key === 'default'} className={`border border-${meta.color}-200 rounded-xl overflow-hidden group`}>
                                                            <summary className={`flex items-center gap-2 px-4 py-3 bg-${meta.color}-50 cursor-pointer hover:bg-${meta.color}-100 transition-colors`}>
                                                                <span className="text-base">{meta.icon}</span>
                                                                <span className="text-sm font-semibold text-stone-800 flex-1">{meta.label}</span>
                                                                <span className="text-[10px] text-stone-400">Click to expand</span>
                                                            </summary>
                                                            <div className="p-4 space-y-3">
                                                                {/* Value Proposition */}
                                                                <div className="p-3 bg-amber-50/50 border border-amber-100 rounded-lg">
                                                                    <p className="text-[10px] font-medium text-amber-600 uppercase tracking-wide mb-1">Value Proposition</p>
                                                                    <p className="text-sm font-medium text-stone-900">{r.value_proposition}</p>
                                                                </div>
                                                                {/* Key sections */}
                                                                <div className="grid grid-cols-2 gap-2">
                                                                    {[{ l: 'What It Is', k: 'what_it_is' }, { l: "Who It's For", k: 'who_it_is_for' }, { l: 'The Problem', k: 'the_problem' }, { l: 'What It Does', k: 'what_it_does' }].map(s => (
                                                                        <div key={s.k} className="p-2 border border-stone-100 rounded-lg">
                                                                            <p className="text-[10px] font-medium text-stone-400 uppercase mb-0.5">{s.l}</p>
                                                                            <p className="text-xs text-stone-700">{r[s.k]}</p>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                {/* Benefits */}
                                                                <div className="grid grid-cols-3 gap-2">
                                                                    {[{ l: 'Functional', k: 'functional_benefits', c: 'text-blue-600' }, { l: 'Business', k: 'business_benefits', c: 'text-emerald-600' }, { l: 'Emotional', k: 'emotional_benefits', c: 'text-purple-600' }].map(b => (
                                                                        <div key={b.k} className="p-2 border border-stone-100 rounded-lg">
                                                                            <p className="text-[10px] font-medium text-stone-400 uppercase mb-1">{b.l}</p>
                                                                            <ul className="space-y-0.5">
                                                                                {(r[b.k] || []).map((item, i) => (
                                                                                    <li key={i} className={`text-[11px] ${b.c} flex items-start gap-1`}>
                                                                                        <Check className="w-3 h-3 shrink-0 mt-0.5" />{item}
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                {/* Short version + Headlines */}
                                                                <div className="grid grid-cols-2 gap-2">
                                                                    <div className="p-2 border border-stone-100 rounded-lg">
                                                                        <p className="text-[10px] font-medium text-stone-400 uppercase mb-0.5">Short Version</p>
                                                                        <p className="text-xs text-stone-700">{r.short_version}</p>
                                                                    </div>
                                                                    <div className="p-2 border border-stone-100 rounded-lg">
                                                                        <p className="text-[10px] font-medium text-stone-400 uppercase mb-0.5">Headlines</p>
                                                                        {(r.headlines || []).slice(0, 3).map((h, i) => (
                                                                            <p key={i} className="text-[11px] text-stone-600">{i + 1}. {h}</p>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </details>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* AI Rewrite Modal */}
                    <ProductAIRewriteModal
                        open={showAIRewriteModal}
                        onClose={() => setShowAIRewriteModal(false)}
                        baseInfoHtml={form.base_info_html}
                        productName={form.name}
                        productCategory={form.category}
                        productId={productId}
                        getAuthHeader={getAuthHeader}
                        onSaveDraft={saveDraft}
                        existingResult={form.ai_rewrite_result}
                        existingPersonaResults={form.ai_persona_results}
                        onCommit={({ description, content_html, features, ai_rewrite_result, ai_persona_results }) => {
                            setForm(prev => {
                                const updated = {
                                    ...prev,
                                    description: description || prev.description,
                                    content_html: content_html || prev.content_html,
                                    ai_rewrite_result: ai_rewrite_result || prev.ai_rewrite_result,
                                    ai_persona_results: ai_persona_results || prev.ai_persona_results,
                                };
                                if (features?.length) {
                                    const existingIdx = prev.sections.findIndex(s => s.type === 'features');
                                    const featuresSection = {
                                        section_id: existingIdx >= 0 ? prev.sections[existingIdx].section_id : `sec_${Date.now()}`,
                                        type: 'features', title: 'Features',
                                        order: existingIdx >= 0 ? prev.sections[existingIdx].order : prev.sections.length,
                                        data: { features }, visible: true,
                                    };
                                    const sections = [...prev.sections];
                                    if (existingIdx >= 0) sections[existingIdx] = featuresSection;
                                    else sections.push(featuresSection);
                                    updated.sections = sections;
                                }
                                return updated;
                            });
                            setOverviewTab('content');
                        }}
                    />

                    {/* Dynamic Sections */}
                    {form.sections.map((section, idx) => (
                        <SectionCard
                            key={section.section_id}
                            section={section}
                            onUpdate={(updated) => updateSection(idx, updated)}
                            onRemove={() => removeSection(idx)}
                            productId={productId}
                            getAuthHeader={getAuthHeader}
                        />
                    ))}

                    {/* Add Section Button */}
                    <div className="relative">
                        <button
                            onClick={() => setShowAddSection(!showAddSection)}
                            className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-stone-200 rounded-xl text-sm text-stone-400 hover:text-stone-600 hover:border-stone-300 transition-colors"
                        >
                            <Plus className="w-4 h-4" /> Add Section
                        </button>
                        {showAddSection && (
                            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-stone-200 rounded-xl shadow-lg py-2 z-10 w-56">
                                {Object.entries(SECTION_TYPES).map(([type, info]) => {
                                    const Icon = info.icon;
                                    return (
                                        <button
                                            key={type}
                                            onClick={() => addSection(type)}
                                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50 transition-colors"
                                        >
                                            <Icon className="w-4 h-4 text-stone-400" />
                                            {info.label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Sidebar */}
                <div className="w-80 flex-shrink-0 space-y-4">
                    {/* Status & Meta */}
                    <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-4">
                        <div>
                            <Label className="text-xs text-stone-500 uppercase tracking-wider font-semibold">Status</Label>
                            <Select value={form.status} onValueChange={v => setForm(prev => ({ ...prev, status: v }))}>
                                <SelectTrigger className="mt-1.5" data-testid="product-status-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        {!isNew && (
                            <div className="flex items-center justify-between py-2 border-t border-stone-100">
                                <span className="text-xs text-stone-500">Published</span>
                                {product?.published_at ? (
                                    <span className="flex items-center gap-1 text-xs text-emerald-600">
                                        <CheckCircle2 className="w-3 h-3" /> Yes
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-1 text-xs text-amber-600">
                                        <Clock className="w-3 h-3" /> Draft
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Tags */}
                    <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
                        <Label className="text-xs text-stone-500 uppercase tracking-wider font-semibold">Tags</Label>
                        <div className="flex flex-wrap gap-1.5">
                            {form.tags.map((tag, idx) => (
                                <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 bg-stone-100 text-stone-600 rounded-full text-xs">
                                    {tag}
                                    <button onClick={() => setForm(prev => ({ ...prev, tags: prev.tags.filter((_, i) => i !== idx) }))}
                                        className="text-stone-400 hover:text-stone-600">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-1.5">
                            <Input
                                value={tagInput}
                                onChange={e => setTagInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                                placeholder="Add tag..."
                                className="text-xs h-8"
                            />
                            <Button variant="outline" size="sm" onClick={addTag} className="h-8 px-2 text-xs">
                                <Plus className="w-3 h-3" />
                            </Button>
                        </div>
                    </div>

                    {/* Featured Image */}
                    <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
                        <Label className="text-xs text-stone-500 uppercase tracking-wider font-semibold">Featured Image</Label>
                        {product?.featured_image_resolved_url ? (
                            <div className="relative group">
                                <img src={product.featured_image_resolved_url} alt="Featured" className="w-full h-32 object-cover rounded-lg" />
                                <button
                                    onClick={() => setForm(prev => ({ ...prev, featured_image_url: null }))}
                                    className="absolute top-1 right-1 p-1 bg-black/50 rounded text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-24 bg-stone-50 border-2 border-dashed border-stone-200 rounded-lg">
                                <p className="text-xs text-stone-400">Upload via Media Gallery section</p>
                            </div>
                        )}
                    </div>

                    {/* Product Details — Category first, then SKU */}
                    <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
                        <Label className="text-xs text-stone-500 uppercase tracking-wider font-semibold">Product Details</Label>
                        <div>
                            <Label className="text-xs text-stone-500">Category</Label>
                            <Select value={form.category} onValueChange={v => handleCategoryChange(v)}>
                                <SelectTrigger className="mt-1" data-testid="product-category-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs text-stone-500">SKU / Product Code</Label>
                            <div className="flex items-center gap-2 mt-1">
                                <Input
                                    value={form.sku}
                                    onChange={e => { setForm(prev => ({ ...prev, sku: e.target.value })); setSkuManual(true); }}
                                    placeholder={skuLoading ? 'Generating...' : 'Select category first'}
                                    className="font-mono text-sm flex-1"
                                    data-testid="product-sku-input"
                                />
                                {isNew && form.category && (
                                    <button
                                        onClick={() => generateSku(form.category)}
                                        className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-50 rounded-lg transition-colors"
                                        title="Regenerate SKU"
                                    >
                                        <RefreshCw className={`w-3.5 h-3.5 ${skuLoading ? 'animate-spin' : ''}`} />
                                    </button>
                                )}
                            </div>
                            {skuError && <p className="text-[10px] text-red-500 mt-1">{skuError}</p>}
                            {!skuError && form.sku && !isNew && <p className="text-[10px] text-stone-400 mt-1">SKU can be edited but must remain unique.</p>}
                        </div>
                    </div>

                    {/* Pricing */}
                    <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
                        <Label className="text-xs text-stone-500 uppercase tracking-wider font-semibold">Pricing</Label>
                        <div>
                            <Label className="text-xs text-stone-500">Pricing Model</Label>
                            <Select value={form.pricing_model} onValueChange={v => setForm(prev => ({ ...prev, pricing_model: v }))}>
                                <SelectTrigger className="mt-1" data-testid="product-pricing-model">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {(form.category === 'software' ? SAAS_PRICING_MODELS : pricingModels).map(m =>
                                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label className="text-xs text-stone-500">Currency</Label>
                                <Select value={form.currency} onValueChange={v => setForm(prev => ({ ...prev, currency: v }))}>
                                    <SelectTrigger className="mt-1 text-xs h-8">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CURRENCIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs text-stone-500">Amount</Label>
                                <div className="relative mt-1">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-stone-400">{currencySymbol(form.currency)}</span>
                                    <Input
                                        value={form.amount}
                                        onChange={e => setForm(prev => ({ ...prev, amount: e.target.value }))}
                                        placeholder="0.00"
                                        className="pl-6 text-xs h-8"
                                        type="number"
                                        step="0.01"
                                        data-testid="product-amount-input"
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Billing Interval — SaaS gets a dropdown, others get free text */}
                        {form.category === 'software' ? (
                            <div>
                                <Label className="text-xs text-stone-500">Billing Interval</Label>
                                <Select value={form.billing_frequency || ''} onValueChange={v => setForm(prev => ({ ...prev, billing_frequency: v }))}>
                                    <SelectTrigger className="mt-1 text-xs h-8">
                                        <SelectValue placeholder="Select interval..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SAAS_BILLING_INTERVALS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        ) : (
                            <div>
                                <Label className="text-xs text-stone-500">Billing Frequency</Label>
                                <Input
                                    value={form.billing_frequency}
                                    onChange={e => setForm(prev => ({ ...prev, billing_frequency: e.target.value }))}
                                    placeholder="e.g., Monthly, Annual"
                                    className="mt-1 text-xs h-8"
                                />
                            </div>
                        )}

                        {/* SaaS-specific fields */}
                        {form.category === 'software' && (
                            <div className="space-y-3 pt-2 border-t border-stone-100">
                                {(form.pricing_model === 'per_seat' || form.pricing_model === 'tiered') && (
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <Label className="text-xs text-stone-500">Min Seats</Label>
                                            <Input
                                                value={form.min_seats || ''}
                                                onChange={e => setForm(prev => ({ ...prev, min_seats: e.target.value }))}
                                                placeholder="1"
                                                className="mt-1 text-xs h-8"
                                                type="number"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-stone-500">Max Seats</Label>
                                            <Input
                                                value={form.max_seats || ''}
                                                onChange={e => setForm(prev => ({ ...prev, max_seats: e.target.value }))}
                                                placeholder="Unlimited"
                                                className="mt-1 text-xs h-8"
                                                type="number"
                                            />
                                        </div>
                                    </div>
                                )}
                                {form.pricing_model === 'freemium' && (
                                    <div>
                                        <Label className="text-xs text-stone-500">Free Tier Limits</Label>
                                        <Input
                                            value={form.free_tier_limits || ''}
                                            onChange={e => setForm(prev => ({ ...prev, free_tier_limits: e.target.value }))}
                                            placeholder="e.g., 5 users, 1GB storage"
                                            className="mt-1 text-xs h-8"
                                        />
                                    </div>
                                )}
                                <div>
                                    <Label className="text-xs text-stone-500">Trial Period</Label>
                                    <Input
                                        value={form.trial_period || ''}
                                        onChange={e => setForm(prev => ({ ...prev, trial_period: e.target.value }))}
                                        placeholder="e.g., 14 days, 30 days"
                                        className="mt-1 text-xs h-8"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-stone-500">Setup Fee</Label>
                                    <div className="relative mt-1">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-stone-400">{currencySymbol(form.currency)}</span>
                                        <Input
                                            value={form.setup_fee || ''}
                                            onChange={e => setForm(prev => ({ ...prev, setup_fee: e.target.value }))}
                                            placeholder="0.00"
                                            className="pl-6 text-xs h-8"
                                            type="number"
                                            step="0.01"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Region Pricing */}
                    <div className="bg-white border border-stone-200 rounded-xl p-4 space-y-3">
                        <Label className="text-xs text-stone-500 uppercase tracking-wider font-semibold">Region Pricing</Label>

                        {form.region_pricing.length > 0 && (
                            <div className="space-y-1.5">
                                {form.region_pricing.map((rp, idx) => (
                                    <div key={idx} className="flex items-center gap-1.5 p-2 bg-stone-50 rounded-lg text-xs">
                                        <MapPin className="w-3 h-3 text-blue-500 flex-shrink-0" />
                                        <span className="font-medium text-stone-700 w-16 truncate">{rp.region}</span>
                                        <Select value={rp.currency} onValueChange={v => updateRegionPricing(idx, 'currency', v)}>
                                            <SelectTrigger className="w-16 h-6 text-[10px] border-stone-200">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {CURRENCIES.map(c => <SelectItem key={c.value} value={c.value}>{c.value}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        <div className="relative flex-1">
                                            <Input
                                                value={rp.amount}
                                                onChange={e => updateRegionPricing(idx, 'amount', e.target.value)}
                                                type="number"
                                                step="0.01"
                                                className="h-6 text-[10px] pl-1"
                                                placeholder="0"
                                            />
                                        </div>
                                        <button onClick={() => removeRegion(idx)} className="p-0.5 hover:bg-red-50 rounded text-stone-400 hover:text-red-500">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {availableRegions.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {availableRegions.map(region => (
                                    <button
                                        key={region.value}
                                        onClick={() => addRegion(region.value)}
                                        className="flex items-center gap-1 px-2 py-1 text-[10px] text-stone-500 border border-dashed border-stone-300 rounded-full hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                    >
                                        <Plus className="w-2.5 h-2.5" />{region.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Version History Slide-over */}
            {showVersionHistory && (
                <ProductVersionHistory
                    productId={productId}
                    getAuthHeader={getAuthHeader}
                    onClose={() => setShowVersionHistory(false)}
                    onRestore={() => { setShowVersionHistory(false); loadProduct(); }}
                />
            )}

            {/* MD Preview Modal */}
            {showMdPreview && (
                <Dialog open={showMdPreview} onOpenChange={() => setShowMdPreview(false)}>
                    <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Eye className="w-5 h-5 text-stone-500" />
                                Agent MD File Preview
                                <span className="text-xs font-normal text-stone-400 ml-2">This is what AI agents read when accessing this product</span>
                            </DialogTitle>
                        </DialogHeader>
                        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg shrink-0">
                            <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                            <div className="text-xs text-blue-700">
                                <p className="font-semibold mb-1">Agents with access to this file:</p>
                                <div className="flex flex-wrap gap-2">
                                    {['Sales Copilot (AI Chat)', 'Meeting Copilot', 'Email Writer', 'Communication Coach', 'Deal Analyzer'].map(a => (
                                        <span key={a} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-medium">{a}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto mt-3">
                            {!mdContent ? (
                                <div className="flex items-center justify-center h-32">
                                    <div className="w-6 h-6 border-2 border-stone-400 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : (
                                <pre className="text-xs text-stone-700 whitespace-pre-wrap font-mono bg-stone-50 p-4 rounded-lg border border-stone-200 leading-relaxed">
                                    {mdContent}
                                </pre>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
